import { Loader } from '@googlemaps/js-api-loader'
import {MarkerClusterer} from "@googlemaps/markerclusterer";

const pin =
	'<svg width="20" height="27" viewBox="0 0 20 27" fill="#004597" xmlns="http://www.w3.org/2000/svg">' +
		'<g id="Map pin">' +
			'<path id="Pin" fill-rule="evenodd" clip-rule="evenodd" d="M10 0.404785C15.5228 0.404785 20 4.88194 20 10.4048C20 11.0789 19.9333 11.7375 19.8061 12.3742C18.9235 19.0254 10.0525 26.5953 10.0525 26.5953C10.0525 26.5953 2.50328 20.1532 0.640267 13.9332C0.226432 12.836 0 11.6468 0 10.4048C0 4.88194 4.47715 0.404785 10 0.404785Z"/>' +
			'<path id="Circle" d="M13.8095 10.4047C13.8095 8.3008 12.1039 6.59521 9.99995 6.59521C7.89601 6.59521 6.19043 8.3008 6.19043 10.4047C6.19043 12.5087 7.89601 14.2143 9.99995 14.2143C12.1039 14.2143 13.8095 12.5087 13.8095 10.4047Z" fill="white"/>' +
		'</g>' +
	'</svg>';

export default class Map {

	static getHolderSelectors = () => ['.map-element'];

	/** @type {HTMLElement} */
	#holderElement;

	/** @type {Loader} */
	#loader;

	/** @type {google.maps.Map} */
	#map;

	/** @type {InfoWindow} */
	#infoWindow;

	/** @param {HTMLElement} holderElement */
	constructor(holderElement) {
		this.#holderElement = holderElement;
		this.#loader = new Loader({
			apiKey: window.config.googleMapsApiKey,
			version: 'weekly',
		});
		this.loadMap().then(this.populateLocations.bind(this));
	}

	async loadMap() {
		const mapOptions = {
			zoom: 8,
			maxZoom: 15,
			center: { lat: -27.455, lng: 153.009 },
			styles: [{"featureType":"landscape.man_made","elementType":"all","stylers":[{"color":"#faf5ed"},{"lightness":"0"},{"gamma":"1"}]},{"featureType":"poi.park","elementType":"geometry.fill","stylers":[{"color":"#bae5a6"}]},{"featureType":"road","elementType":"all","stylers":[{"weight":"1.00"},{"gamma":"1.8"},{"saturation":"0"}]},{"featureType":"road","elementType":"geometry.fill","stylers":[{"hue":"#ffb200"}]},{"featureType":"road.arterial","elementType":"geometry.fill","stylers":[{"lightness":"0"},{"gamma":"1"}]},{"featureType":"transit.station.airport","elementType":"all","stylers":[{"hue":"#b000ff"},{"saturation":"23"},{"lightness":"-4"},{"gamma":"0.80"}]},{"featureType":"water","elementType":"all","stylers":[{"color":"#a0daf2"}]}],
			mapId: "MAP_ELEMENT_MAP",
			mapTypeId: 'terrain',
			fullscreenControl: false,
			streetViewControl: false,
			mapTypeControl: false,
		};
		const { Map, InfoWindow } = await this.#loader.importLibrary("maps");
		this.#map = new Map(this.#holderElement.querySelector('.map'), mapOptions);
		this.#infoWindow = new InfoWindow({
			content: "",
			disableAutoPan: true,
			maxWidth: 200,
		});
	}

	async populateLocations() {
		const response = await fetch('/api/places');
		const locations = await response.json();
		const holder = this.#holderElement.querySelector('.locations');
		const x = [];
		const categories = [];
		const filterTab = [];

		filterTab.push(`<div class="filter-tab-col"><div class="filter-tab">`);

		for (const location of locations) {
			location.categories.forEach(category => {
				let isCategoryExisted = false;
				const button = `<button data-location-id="${location.id}" data-category="category-${category.id}"><h5>${location.name}</h5>${location.excerpt}</button>`;
				x.push(button);

				categories.forEach(cate => {
					if (cate.id === category.id) {
						isCategoryExisted = true;
					}
				});

				if (!isCategoryExisted) {
					if (category.id === parseInt(holder.dataset.defaultCategory)) {
						filterTab.push(`<span class="category category-${category.id} is-active" data-category="category-${category.id}">${category.name}</span>`);
					} else {
						filterTab.push(`<span class="category category-${category.id}" data-category="category-${category.id}">${category.name}</span>`);
					}

					categories.push(category);
				}
			});
		}

		filterTab.push(`</div></div>`);
		holder.innerHTML = filterTab.join('') + x.join('');
		this.#handleLocationDisplay();

		holder.querySelectorAll('.category').forEach(category => {
			category.addEventListener('click', () => {
				holder.querySelector('.category.is-active').classList.remove('is-active');
				category.classList.add('is-active');
				this.#handleLocationDisplay();
			});
		});

		const { AdvancedMarkerElement } = await this.#loader.importLibrary("marker");
		const { LatLngBounds } = await this.#loader.importLibrary("core");
		const bounds = new LatLngBounds();

		const markers = locations.map((location) => {
			const position = {lat: location.latitude, lng: location.longitude};
			const parser = new DOMParser();
			const pinSvg = parser.parseFromString(pin, 'image/svg+xml').documentElement;
			const marker = new AdvancedMarkerElement({
				position,
				content: pinSvg,
			});
			bounds.extend(position);

			marker.addListener("click", () => {
				this.#infoWindow.setContent(`<h5>${location.name}</h5><p>${location.excerpt}</p>`);
				this.#infoWindow.open(map, marker);
			});
			return {
				location,
				marker,
			};
		});

		this.#map.fitBounds(bounds, {
			top: 0,
			right: 0,
			bottom: 0,
			left: 0,
		});

		const renderer = {
			render: ({ count, position }, stats) => {
				const svg = window.btoa(`
					<svg fill="#004597" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 240 240">
						<circle cx="120" cy="120" r="70" />
					</svg>`);
				return new google.maps.Marker({
					position,
					icon: {
						url: `data:image/svg+xml;base64,${svg}`,
						scaledSize: new google.maps.Size(75, 75),
					},
					label: {
						text: String(count),
						color: "rgba(255,255,255,0.9)",
						fontSize: "12px",
					},
					zIndex: Number(google.maps.Marker.MAX_ZINDEX) + count,
				});
			}
		};


		new MarkerClusterer({
			markers: markers.map(m => m.marker),
			map: this.#map,
			renderer,
		});

		holder.querySelectorAll('button').forEach(button => {
			button.addEventListener('click', (event) => {
				const id = parseInt(event.currentTarget.dataset.locationId);
				const marker = markers.find(m => m.location.id === id);
				if (marker) {
					const bounds = new LatLngBounds();
					bounds.extend({lat: marker.location.latitude, lng: marker.location.longitude});
					this.#map.fitBounds(bounds);
					if (this.#isMobile()) {
						this.#map.panBy(-1 * (holder.offsetWidth + holder.offsetLeft) / 2, 0);
					}
					this.#infoWindow.setContent(`<h5>${marker.location.name}</h5><p>${marker.location.excerpt}</p>`);
					this.#infoWindow.open(this.#map, marker.marker);
				}
			});
		});

	}

	#handleLocationDisplay() {
		const holder = this.#holderElement.querySelector('.locations');
		let currentCategory = holder.querySelector('.category.is-active');

		holder.querySelectorAll('button').forEach(button => {
			if (currentCategory.dataset.category !== button.dataset.category) {
				button.style.display = 'none';
			} else {
				button.style.display = 'inline-block';
			}
		});
	}

	#isMobile() {
		return window.outerWidth > 992;
	}

}
